// Welcome → Menu tape transport — the visuals.
//
// Extracted from the original all-in-one TapeTransition when a second sequence
// arrived. Orchestration (clock, commit, watchdog, skip, teardown) now lives in
// TransitionHost; this file is only the performance.
//
// WHY THIS PAINTS IMPERATIVELY
// ----------------------------
// Every other animation in the app is declarative framer-motion, and this one
// started that way too. It did not work: inside the portal framer applied
// `initial` and then never started an animation — verified by sampling, inline
// styles were written exactly once, `getAnimations()` returned empty, and a
// trivial x: 0 → 300 control element was frozen too. The cause is
// environmental: requestAnimationFrame doesn't fire in an occluded window, and
// framer's keyframe tracks with `times` and percentage transforms fall back to
// a main-thread rAF loop, whereas its simple animations survive on the
// compositor via WAAPI.
//
// Painting from one explicit clock is better here regardless: the whole
// performance becomes a pure function of elapsed time, so it is reproducible,
// frame-rate independent, unit-tested without a DOM, and scrubable.

import { useRef } from 'react';
import { useTransitionStore, type Rect } from '../../stores/transition';
import { type TapeTimeline } from '../../theme/tapeTimeline';
import { beatEnd, sampleTrack, type Stop } from '../../theme/sequence';
import { easeFn } from '../../theme/motion';
import { colors } from '../../theme/tokens';
import { Cassette } from '../procedural/Cassette';
import { useSequenceFrame } from './clock';

// Deterministic streak layout. Math.random() at render would reshuffle the
// wow-and-flutter lines every frame; a fixed LCG gives the same scatter every
// run, which also makes the sequence reproducible in screenshots.
const STREAKS = (() => {
  let s = 0x9e3779b9;
  const rnd = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
  return Array.from({ length: 16 }, () => ({
    top: 4 + rnd() * 92,
    width: 6 + rnd() * 34,
    speed: 1.6 + rnd() * 2.4, // screen-widths per second
    offset: rnd(),
    opacity: 0.12 + rnd() * 0.5,
    sodium: rnd() > 0.62,
  }));
})();

interface Placement {
  x: number;
  y: number;
  scale: number;
}

/** Centre an element of natural size w×h on a target rect. */
function place(rect: Rect, w: number, h: number): Placement {
  return {
    x: rect.x + rect.width / 2 - w / 2,
    y: rect.y + rect.height / 2 - h / 2,
    scale: h > 0 ? rect.height / h : 1,
  };
}

export function TapeTransportRenderer({ score }: { score: TapeTimeline }) {
  const bandTop = useRef<HTMLDivElement | null>(null);
  const bandBottom = useRef<HTMLDivElement | null>(null);
  const head = useRef<HTMLDivElement | null>(null);
  const cassette = useRef<HTMLDivElement | null>(null);
  const bloom = useRef<HTMLDivElement | null>(null);
  const streaks = useRef<HTMLDivElement | null>(null);
  const mark = useRef<HTMLDivElement | null>(null);

  const spoolRate = useRef(score.spoolTurns);
  const markGeom = useRef<{ from: Placement; to: Placement } | null>(null);
  const tracks = useRef<Tracks | null>(null);
  if (!tracks.current || tracks.current.forTotal !== score.total) {
    tracks.current = buildTracks(score);
  }

  useSequenceFrame((elapsed) => {
    const tr = tracks.current;
    if (!tr) return;

    // Reels coast down once the cover starts retracting.
    spoolRate.current = elapsed >= score.settle.at ? 0 : score.spoolTurns;

    // The destination registers its landing slot after the commit; capture the
    // geometry on the first frame both ends are known.
    if (!markGeom.current && mark.current) {
      const { slots } = useTransitionStore.getState();
      const from = slots.title;
      const to = slots.wordmark;
      if (from && to) {
        const box = mark.current.getBoundingClientRect();
        if (box.width > 0) {
          markGeom.current = {
            from: place(from, box.width, box.height),
            to: place(to, box.width, box.height),
          };
        }
      }
    }

    const shut = sampleTrack(tr.band, elapsed);
    const bandOpacity = sampleTrack(tr.bandOpacity, elapsed);

    if (bandTop.current) {
      bandTop.current.style.transform = score.travels
        ? `translateY(${-100 + shut * 100}%)`
        : 'translateY(0%)';
      bandTop.current.style.opacity = String(bandOpacity);
    }
    if (bandBottom.current) {
      bandBottom.current.style.transform = score.travels
        ? `translateY(${100 - shut * 100}%)`
        : 'translateY(0%)';
      bandBottom.current.style.opacity = String(bandOpacity);
    }
    if (head.current) {
      head.current.style.opacity = String(sampleTrack(tr.head, elapsed));
      head.current.style.transform = `scaleX(${sampleTrack(tr.headScale, elapsed)})`;
    }
    if (cassette.current) {
      cassette.current.style.opacity = String(sampleTrack(tr.cassetteOpacity, elapsed));
      cassette.current.style.transform = `scale(${sampleTrack(tr.cassetteScale, elapsed)})`;
    }
    if (bloom.current) {
      bloom.current.style.opacity = String(sampleTrack(tr.bloom, elapsed));
    }
    if (streaks.current) {
      streaks.current.style.opacity = String(sampleTrack(tr.streaks, elapsed));
      // Streaks race independently of the score — they're texture, not beats.
      const kids = streaks.current.children;
      for (let i = 0; i < kids.length; i++) {
        const l = STREAKS[i];
        const p = (l.offset + elapsed * l.speed) % 1;
        (kids[i] as HTMLElement).style.transform = `translateX(${110 - p * 230}vw)`;
      }
    }
    if (mark.current) {
      const geom = markGeom.current;
      if (!geom) {
        mark.current.style.opacity = '0';
      } else {
        const p = sampleTrack(tr.mark, elapsed);
        // Reduced motion: no travel. Sit at the destination and resolve there.
        const from = score.travels ? geom.from : geom.to;
        const x = from.x + (geom.to.x - from.x) * p;
        const y = from.y + (geom.to.y - from.y) * p;
        const s = from.scale + (geom.to.scale - from.scale) * p;
        mark.current.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
        mark.current.style.opacity = score.travels ? '1' : String(p);
      }
    }
  });

  return (
    <>
      {/* Tape: two bands closing over the screen, leaving a slit that shuts. */}
      <div
        ref={bandTop}
        className="absolute inset-x-0 top-0 h-1/2"
        style={{ backgroundColor: colors.void, transform: 'translateY(-100%)' }}
      >
        <div
          className="absolute inset-x-0 bottom-0 h-[2px]"
          style={{ backgroundColor: colors.sodium, opacity: 0.55 }}
        />
      </div>
      <div
        ref={bandBottom}
        className="absolute inset-x-0 bottom-0 h-1/2"
        style={{ backgroundColor: colors.void, transform: 'translateY(100%)' }}
      >
        <div
          className="absolute inset-x-0 top-0 h-[2px]"
          style={{ backgroundColor: colors.sodium, opacity: 0.55 }}
        />
      </div>

      {/* Wow and flutter — what sells "the tape is MOVING" rather than "a black
          rectangle appeared". Dropped entirely on reduced motion. */}
      {score.streakCount > 0 && (
        <div ref={streaks} className="absolute inset-0" style={{ opacity: 0 }}>
          {STREAKS.slice(0, score.streakCount).map((l, i) => (
            <div
              key={i}
              className="absolute h-[2px] rounded-full"
              style={{
                top: `${l.top}%`,
                width: `${l.width}vw`,
                backgroundColor: l.sodium ? colors.sodium : colors.marker,
                opacity: l.opacity,
              }}
            />
          ))}
        </div>
      )}

      {/* Playback head — a hot line where the bands meet. */}
      <div className="absolute inset-x-0 top-1/2 -mt-[1.5px]">
        <div
          ref={head}
          className="h-[3px] w-full"
          style={{
            background: `linear-gradient(90deg, transparent, ${colors.sodiumGlow}, transparent)`,
            opacity: 0,
          }}
        />
      </div>

      {/* The cassette, seated in the transport at centre frame. Centred by a
          static flex parent: framer and our own writes both set `transform`
          wholesale, so a Tailwind translate on the animated element would be
          silently overwritten. */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          ref={cassette}
          className="w-[46vw] max-w-[420px]"
          style={{ opacity: 0, transform: 'scale(0.72)' }}
        >
          <Cassette rateRef={spoolRate} tier={score.tier} className="h-auto w-full" />
        </div>
      </div>

      {/* Sodium flash on the impact frame. */}
      <div
        ref={bloom}
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, rgba(255,176,74,0.5) 0%, rgba(255,138,61,0) 62%)',
          opacity: 0,
        }}
      />

      {/* The shared element: Welcome's h1 travelling to Menu's header wordmark.
          Rendered at a fixed size and transformed into place, so its natural box
          can be measured on the first frame. */}
      <div
        ref={mark}
        className="absolute left-0 top-0 flex items-baseline gap-[0.4em] whitespace-nowrap leading-none"
        style={{ fontSize: 48, transformOrigin: 'center', opacity: 0 }}
      >
        <span className="font-cholo text-cholo">Cholos</span>
        <span className="font-data text-[0.55em] uppercase tracking-[0.2em] text-sodium">
          vs.
        </span>
        <span className="font-emo text-emo">Emos</span>
      </div>
    </>
  );
}

interface Tracks {
  forTotal: number;
  band: Stop[];
  bandOpacity: Stop[];
  head: Stop[];
  headScale: Stop[];
  cassetteOpacity: Stop[];
  cassetteScale: Stop[];
  bloom: Stop[];
  streaks: Stop[];
  mark: Stop[];
}

/**
 * The score, per property. Each is a list of (time, value) stops with an easing
 * into each stop.
 */
function buildTracks(t: TapeTimeline): Tracks {
  const coverShut = beatEnd(t.cover);
  const travels = t.travels;

  return {
    forTotal: t.total,
    // 0 = open (off screen), 1 = shut. Only used when travelling.
    band: [
      { at: 0, v: 0 },
      { at: t.cover.at, v: 0 },
      { at: coverShut, v: 1, ease: easeFn.transport },
      { at: t.settle.at, v: 1 },
      { at: t.total, v: 0, ease: easeFn.out },
    ],
    // Reduced motion dissolves the cover in place instead of sliding it.
    bandOpacity: travels
      ? [{ at: 0, v: 1 }]
      : [
          { at: 0, v: 0 },
          { at: t.cover.at, v: 0 },
          { at: coverShut, v: 1, ease: easeFn.out },
          { at: t.settle.at, v: 1 },
          { at: t.total, v: 0, ease: easeFn.out },
        ],
    head: [
      { at: 0, v: 0 },
      { at: t.cover.at, v: 0 },
      { at: coverShut, v: 1, ease: easeFn.out },
      { at: t.settle.at, v: 0.7 },
      { at: t.total, v: 0, ease: easeFn.in },
    ],
    headScale: [
      { at: 0, v: 0.2 },
      { at: t.cover.at, v: 0.4 },
      { at: coverShut, v: 1, ease: easeFn.out },
      { at: t.settle.at, v: 1 },
      { at: t.total, v: 0.3, ease: easeFn.in },
    ],
    cassetteOpacity: [
      { at: 0, v: 0 },
      { at: beatEnd(t.spinUp), v: 1, ease: easeFn.out },
      { at: t.settle.at, v: 1 },
      { at: t.total, v: 0, ease: easeFn.in },
    ],
    cassetteScale: travels
      ? [
          { at: 0, v: 0.72 },
          { at: beatEnd(t.spinUp), v: 1, ease: easeFn.out },
          { at: t.settle.at, v: 1.04 },
          { at: t.total, v: 1.14, ease: easeFn.in },
        ]
      : [{ at: 0, v: 1 }],
    bloom: [
      { at: 0, v: 0 },
      { at: t.commitAt - 0.06, v: 0 },
      { at: t.commitAt, v: 0.9, ease: easeFn.out },
      { at: t.commitAt + 0.3, v: 0, ease: easeFn.in },
    ],
    streaks: [
      { at: 0, v: 0 },
      { at: t.scrub.at, v: 0 },
      { at: t.scrub.at + t.scrub.dur * 0.18, v: 1, ease: easeFn.out },
      { at: beatEnd(t.scrub), v: 1 },
      { at: beatEnd(t.scrub) + 0.12, v: 0, ease: easeFn.in },
    ],
    // Progress of the travelling wordmark, 0 at source, 1 at destination.
    mark: [
      { at: 0, v: 0 },
      { at: t.thread.at, v: 0 },
      { at: beatEnd(t.thread), v: 1, ease: easeFn.transport },
    ],
  };
}
